import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface EngagementMetrics {
  totalBooths: number;
  activeBooths: number;
  totalParticipants: number;
  totalPops: number;
  totalMatches: number;
  totalJoinRequests: number;
  acceptedRequests: number;
  rejectedRequests: number;
}

interface HourlyStats {
  hour: number;
  participants: number;
  pops: number;
  matches: number;
}

interface ConversionRates {
  popToMatchRate: number;
  requestAcceptRate: number;
  participantEngagementRate: number;
}

export interface ExhibitionAnalytics {
  engagement: EngagementMetrics;
  peakHours: HourlyStats[];
  conversions: ConversionRates;
  topPerformingCities: { cityName: string; flag: string; score: number }[];
}

export const useExhibitionAnalytics = () => {
  const [analytics, setAnalytics] = useState<ExhibitionAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch booth metrics
      const { data: allBooths } = await supabase
        .from("live_arenas")
        .select("id, is_active, created_at")
        .eq("is_exhibition_booth", true);

      const totalBooths = allBooths?.length || 0;
      const activeBooths = allBooths?.filter(b => b.is_active)?.length || 0;

      // Fetch participant counts
      const { count: totalParticipants } = await supabase
        .from("arena_participants")
        .select("*", { count: "exact", head: true });

      // Fetch pops in exhibition context
      const { count: totalPops } = await supabase
        .from("pops")
        .select("*", { count: "exact", head: true })
        .eq("tab_context", "love_exhibition");

      // Fetch matches in exhibition context
      const { count: totalMatches } = await supabase
        .from("matches")
        .select("*", { count: "exact", head: true })
        .eq("met_context", "love_exhibition");

      // Fetch join requests
      const { data: joinRequests } = await supabase
        .from("exhibition_join_requests")
        .select("status");

      const totalJoinRequests = joinRequests?.length || 0;
      const acceptedRequests = joinRequests?.filter(r => r.status === "accepted")?.length || 0;
      const rejectedRequests = joinRequests?.filter(r => r.status === "rejected")?.length || 0;

      // Calculate hourly stats (last 24 hours)
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      
      const { data: recentParticipants } = await supabase
        .from("arena_participants")
        .select("joined_at")
        .gte("joined_at", yesterday.toISOString());

      const { data: recentPops } = await supabase
        .from("pops")
        .select("created_at")
        .eq("tab_context", "love_exhibition")
        .gte("created_at", yesterday.toISOString());

      const { data: recentMatches } = await supabase
        .from("matches")
        .select("created_at")
        .eq("met_context", "love_exhibition")
        .gte("created_at", yesterday.toISOString());

      // Group by hour
      const hourlyStats: HourlyStats[] = Array.from({ length: 24 }, (_, i) => ({
        hour: i,
        participants: 0,
        pops: 0,
        matches: 0,
      }));

      recentParticipants?.forEach(p => {
        const hour = new Date(p.joined_at).getHours();
        hourlyStats[hour].participants++;
      });

      recentPops?.forEach(p => {
        const hour = new Date(p.created_at).getHours();
        hourlyStats[hour].pops++;
      });

      recentMatches?.forEach(m => {
        const hour = new Date(m.created_at).getHours();
        hourlyStats[hour].matches++;
      });

      // Calculate conversion rates
      const popToMatchRate = totalPops && totalPops > 0 
        ? ((totalMatches || 0) / totalPops) * 100 
        : 0;
      
      const requestAcceptRate = totalJoinRequests > 0 
        ? (acceptedRequests / totalJoinRequests) * 100 
        : 0;
      
      const participantEngagementRate = (totalParticipants || 0) > 0 
        ? ((totalPops || 0) / (totalParticipants || 1)) * 100 
        : 0;

      // Get top performing cities
      const { data: citiesWithBooths } = await supabase
        .from("live_arenas")
        .select(`
          city_id,
          cities!inner (
            name,
            countries!inner (
              flag
            )
          )
        `)
        .eq("is_exhibition_booth", true)
        .eq("is_active", true);

      const cityScores: Record<string, { name: string; flag: string; count: number }> = {};
      citiesWithBooths?.forEach((booth: any) => {
        const cityId = booth.city_id;
        if (cityId && booth.cities) {
          if (!cityScores[cityId]) {
            cityScores[cityId] = {
              name: booth.cities.name,
              flag: booth.cities.countries?.flag || "🌍",
              count: 0,
            };
          }
          cityScores[cityId].count++;
        }
      });

      const topPerformingCities = Object.values(cityScores)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)
        .map(c => ({ cityName: c.name, flag: c.flag, score: c.count }));

      setAnalytics({
        engagement: {
          totalBooths,
          activeBooths,
          totalParticipants: totalParticipants || 0,
          totalPops: totalPops || 0,
          totalMatches: totalMatches || 0,
          totalJoinRequests,
          acceptedRequests,
          rejectedRequests,
        },
        peakHours: hourlyStats,
        conversions: {
          popToMatchRate,
          requestAcceptRate,
          participantEngagementRate,
        },
        topPerformingCities,
      });
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    analytics,
    loading,
    refetch: fetchAnalytics,
  };
};
